// pages/PublicDocumentUpload/PublicDocumentUpload.js
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Alert,
  Spinner,
  ProgressBar,
  Badge,
} from "react-bootstrap";
import {
  FaIdCard,
  FaFileInvoice,
  FaBuilding,
  FaUniversity,
  FaPhone,
  FaSave,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaUpload,
  FaEye,
  FaTrash,
  FaFile,
  FaUser,
  FaBriefcase,
  FaMapMarkerAlt,
  FaBank,
  FaFileAlt,
} from "react-icons/fa";
import axios from "axios";
import "./publicDocumentUpload.css";

const API_URL = "https://api.brandelvendor.starlighttechlabsindia.com/api/seller";

const PublicDocumentUpload = () => {
  const { trackingId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [documentStatus, setDocumentStatus] = useState("draft");
  const [lastSaved, setLastSaved] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");

  const [formData, setFormData] = useState({
    aadhaar: { number: "", frontImage: "", backImage: "" },
    pan: { number: "", image: "" },
    gst: { number: "", certificate: "" },
    bank: {
      accountHolderName: "",
      accountNumber: "",
      confirmAccountNumber: "",
      ifscCode: "",
      bankName: "",
      branchName: "",
      upiId: "",
    },
    contact: {
      phone: "",
      alternatePhone: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
    business: {
      registrationType: "",
      registrationNumber: "",
      certificate: "",
    },
  });

  const autoSaveTimer = useRef(null);

  // ================= FETCH DOCUMENTS BY TRACKING ID =================
  useEffect(() => {
    const fetchDocument = async () => {
      if (!trackingId) {
        setError("Invalid link. Please check your email.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/documents/${trackingId}`);

        if (res.data.success && res.data.document) {
          const doc = res.data.document;
          setEmail(doc.email);
          setCompany(doc.company);
          setDocumentStatus(doc.status || "draft");
          setLastSaved(doc.lastSaved);
          
          setFormData({
            aadhaar: doc.aadhaar || { number: "", frontImage: "", backImage: "" },
            pan: doc.pan || { number: "", image: "" },
            gst: doc.gst || { number: "", certificate: "" },
            bank: doc.bank || {
              accountHolderName: "",
              accountNumber: "",
              confirmAccountNumber: "",
              ifscCode: "",
              bankName: "",
              branchName: "",
              upiId: "",
            },
            contact: doc.contact || {
              phone: "",
              alternatePhone: "",
              address: "",
              city: "",
              state: "",
              pincode: "",
              country: "India",
            },
            business: doc.business || {
              registrationType: "",
              registrationNumber: "",
              certificate: "",
            },
          });
        } else {
          setError("Document not found. Please check your link.");
        }
      } catch (err) {
        console.error("Error fetching document:", err);
        setError(err.response?.data?.message || "Failed to load document");
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [trackingId]);

  // ================= AUTO-SAVE =================
  const autoSave = async () => {
    if (saving || documentStatus === 'verified' || documentStatus === 'submitted') return;

    try {
      setSaving(true);
      const res = await axios.post(
        `${API_URL}/documents/save`,
        { trackingId, ...formData },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (res.data.success) {
        setLastSaved(new Date());
        setDocumentStatus(res.data.document.status);
        setSuccess("Auto-saved successfully");
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      console.error("Auto-save error:", err);
    } finally {
      setSaving(false);
    }
  };

  // Auto-save on field change with debounce
  const handleFieldChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }

    autoSaveTimer.current = setTimeout(() => {
      autoSave();
    }, 2000);
  };

  // ================= FILE UPLOAD =================
  const handleFileUpload = async (section, field, file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError("Only images and PDF files are allowed");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("File size must be less than 10MB");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("trackingId", trackingId);
    formData.append("field", `${section}.${field}`);

    try {
      const res = await axios.post(
        `${API_URL}/documents/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(progress);
          },
        }
      );

      if (res.data.success) {
        setFormData((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            [field]: res.data.fileUrl,
          },
        }));
        setSuccess("File uploaded successfully");
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.message || "Failed to upload file");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ================= REMOVE FILE =================
  const handleRemoveFile = (section, field) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: "",
      },
    }));
    setSuccess("File removed");
    setTimeout(() => setSuccess(""), 3000);
  };

  // ================= SUBMIT DOCUMENTS =================
  const handleSubmit = async () => {
    const required = [
      { section: 'aadhaar', field: 'number', label: 'Aadhaar Number' },
      { section: 'pan', field: 'number', label: 'PAN Number' },
      { section: 'bank', field: 'accountNumber', label: 'Bank Account Number' },
      { section: 'bank', field: 'ifscCode', label: 'IFSC Code' },
      { section: 'contact', field: 'phone', label: 'Phone Number' },
    ];

    const missing = [];
    for (const req of required) {
      if (!formData[req.section][req.field]) {
        missing.push(req.label);
      }
    }

    if (missing.length > 0) {
      setError(`Please fill all required fields: ${missing.join(', ')}`);
      return;
    }

    if (formData.bank.accountNumber !== formData.bank.confirmAccountNumber) {
      setError("Bank account numbers do not match");
      return;
    }

    if (!window.confirm("Are you sure you want to submit these documents for verification?")) {
      return;
    }

    setSubmitting(true);

    try {
      const res = await axios.post(
        `${API_URL}/documents/submit`,
        { trackingId },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.data.success) {
        setDocumentStatus('submitted');
        setSuccess("✅ Documents submitted successfully for verification!");
        setTimeout(() => setSuccess(""), 5000);
      }
    } catch (err) {
      console.error("Submit error:", err);
      setError(err.response?.data?.message || "Failed to submit documents");
    } finally {
      setSubmitting(false);
    }
  };

  // ================= GET STATUS BADGE =================
  const getStatusBadge = () => {
    switch (documentStatus) {
      case 'verified':
        return <span className="status-badge verified"><FaCheckCircle /> Verified</span>;
      case 'submitted':
        return <span className="status-badge pending"><FaClock /> Pending Verification</span>;
      case 'rejected':
        return <span className="status-badge rejected"><FaTimesCircle /> Rejected</span>;
      default:
        return <span className="status-badge draft"><FaClock /> Draft</span>;
    }
  };

  // ================= FILE PREVIEW COMPONENT =================
  const FilePreview = ({ url, onRemove, onView, isDisabled }) => {
    if (!url) return null;
    
    const fileName = url.split('/').pop();
    const fileSize = Math.round(Math.random() * 500 + 100); // Mock size, replace with actual if available
    
    return (
      <div className="file-preview">
        <FaFile className="text-primary" size={24} />
        <div className="file-info">
          <div className="file-name">{fileName}</div>
          <div className="file-size">{fileSize} KB</div>
        </div>
        <div className="file-actions">
          <button 
            className="btn-icon view" 
            onClick={onView}
            title="View file"
          >
            <FaEye />
          </button>
          {!isDisabled && (
            <button 
              className="btn-icon remove" 
              onClick={onRemove}
              title="Remove file"
            >
              <FaTrash />
            </button>
          )}
        </div>
      </div>
    );
  };

  // ================= RENDER =================
  if (loading) {
    return (
      <section className="document-upload-page">
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Loading your document form...</p>
        </Container>
      </section>
    );
  }

  if (error && !email) {
    return (
      <section className="document-upload-page">
        <Container className="py-5">
          <Alert variant="danger" className="text-center">
            <h4 className="mb-3">❌ Invalid Link</h4>
            <p>{error}</p>
            <Button variant="primary" onClick={() => navigate('/')}>
              Go to Home
            </Button>
          </Alert>
        </Container>
      </section>
    );
  }

  const isDisabled = documentStatus === 'verified' || documentStatus === 'submitted';

  return (
    <section className="document-upload-page">
      <Container>
        {/* Header */}
        <div className="section-header d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h2>
              <FaIdCard className="me-2" />
              Seller Verification Documents
            </h2>
            <div className="subtitle">
              <strong>Email:</strong> {email} &nbsp;|&nbsp; <strong>Company:</strong> {company}
              <br />
              <small className="text-muted">Tracking ID: <strong>{trackingId}</strong></small>
            </div>
          </div>
          <div className="status-area">
            {getStatusBadge()}
            {lastSaved && (
              <div className="text-muted small mt-1">
                Last saved: {new Date(lastSaved).toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {/* Alerts */}
        {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible>{success}</Alert>}

        {/* Status Alerts */}
        {documentStatus === 'verified' && (
          <Alert variant="success" className="mb-4">
            <FaCheckCircle className="me-2" />
            <strong>Congratulations!</strong> Your documents have been verified.
          </Alert>
        )}

        {documentStatus === 'rejected' && (
          <Alert variant="danger" className="mb-4">
            <FaTimesCircle className="me-2" />
            <strong>Documents Rejected.</strong> Please review and resubmit.
          </Alert>
        )}

        {documentStatus === 'submitted' && (
          <Alert variant="warning" className="mb-4">
            <FaClock className="me-2" />
            <strong>Documents Submitted.</strong> Our team is reviewing your documents.
          </Alert>
        )}

        {/* Form Cards */}
        <Row className="g-4">
          {/* AADHAAR CARD SECTION */}
          <Col md={6}>
            <Card className="form-card aadhaar">
              <Card.Header>
                <FaIdCard /> Aadhaar Card
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Aadhaar Number <span className="required">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter 12-digit Aadhaar number"
                    value={formData.aadhaar.number}
                    onChange={(e) => handleFieldChange('aadhaar', 'number', e.target.value)}
                    disabled={isDisabled}
                    maxLength={12}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Front Image</Form.Label>
                  <div className="file-input-wrapper">
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload('aadhaar', 'frontImage', e.target.files[0])}
                      disabled={isDisabled}
                    />
                  </div>
                  <FilePreview
                    url={formData.aadhaar.frontImage}
                    onView={() => window.open(`https://api.brandelvendor.starlighttechlabsindia.com${formData.aadhaar.frontImage}`, '_blank')}
                    onRemove={() => handleRemoveFile('aadhaar', 'frontImage')}
                    isDisabled={isDisabled}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Back Image</Form.Label>
                  <div className="file-input-wrapper">
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload('aadhaar', 'backImage', e.target.files[0])}
                      disabled={isDisabled}
                    />
                  </div>
                  <FilePreview
                    url={formData.aadhaar.backImage}
                    onView={() => window.open(`https://api.brandelvendor.starlighttechlabsindia.com${formData.aadhaar.backImage}`, '_blank')}
                    onRemove={() => handleRemoveFile('aadhaar', 'backImage')}
                    isDisabled={isDisabled}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          {/* PAN CARD SECTION */}
          <Col md={6}>
            <Card className="form-card pan">
              <Card.Header>
                <FaFileInvoice /> PAN Card
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>PAN Number <span className="required">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter 10-digit PAN number"
                    value={formData.pan.number}
                    onChange={(e) => handleFieldChange('pan', 'number', e.target.value)}
                    disabled={isDisabled}
                    maxLength={10}
                    style={{ textTransform: 'uppercase' }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>PAN Card Image</Form.Label>
                  <div className="file-input-wrapper">
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload('pan', 'image', e.target.files[0])}
                      disabled={isDisabled}
                    />
                  </div>
                  <FilePreview
                    url={formData.pan.image}
                    onView={() => window.open(`https://api.brandelvendor.starlighttechlabsindia.com${formData.pan.image}`, '_blank')}
                    onRemove={() => handleRemoveFile('pan', 'image')}
                    isDisabled={isDisabled}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          {/* GST CERTIFICATE SECTION */}
          <Col md={6}>
            <Card className="form-card gst">
              <Card.Header>
                <FaBuilding /> GST Certificate <small className="ms-2 opacity-75">(Optional)</small>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>GST Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter 15-digit GST number"
                    value={formData.gst.number}
                    onChange={(e) => handleFieldChange('gst', 'number', e.target.value)}
                    disabled={isDisabled}
                    maxLength={15}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>GST Certificate</Form.Label>
                  <div className="file-input-wrapper">
                    <Form.Control
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => handleFileUpload('gst', 'certificate', e.target.files[0])}
                      disabled={isDisabled}
                    />
                  </div>
                  <FilePreview
                    url={formData.gst.certificate}
                    onView={() => window.open(`https://api.brandelvendor.starlighttechlabsindia.com${formData.gst.certificate}`, '_blank')}
                    onRemove={() => handleRemoveFile('gst', 'certificate')}
                    isDisabled={isDisabled}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          {/* BANK ACCOUNT SECTION */}
          <Col md={6}>
            <Card className="form-card bank">
              <Card.Header>
                <FaUniversity /> Bank Account Details
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Account Holder Name <span className="required">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter account holder name"
                    value={formData.bank.accountHolderName}
                    onChange={(e) => handleFieldChange('bank', 'accountHolderName', e.target.value)}
                    disabled={isDisabled}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Account Number <span className="required">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter bank account number"
                    value={formData.bank.accountNumber}
                    onChange={(e) => handleFieldChange('bank', 'accountNumber', e.target.value)}
                    disabled={isDisabled}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Confirm Account Number <span className="required">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Re-enter bank account number"
                    value={formData.bank.confirmAccountNumber}
                    onChange={(e) => handleFieldChange('bank', 'confirmAccountNumber', e.target.value)}
                    isInvalid={formData.bank.accountNumber !== formData.bank.confirmAccountNumber && formData.bank.confirmAccountNumber !== ''}
                    disabled={isDisabled}
                  />
                  {formData.bank.accountNumber !== formData.bank.confirmAccountNumber && formData.bank.confirmAccountNumber !== '' && (
                    <Form.Text className="danger">Account numbers do not match</Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>IFSC Code <span className="required">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter IFSC code (e.g., SBIN0001234)"
                    value={formData.bank.ifscCode}
                    onChange={(e) => handleFieldChange('bank', 'ifscCode', e.target.value)}
                    disabled={isDisabled}
                    style={{ textTransform: 'uppercase' }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Bank Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter bank name"
                    value={formData.bank.bankName}
                    onChange={(e) => handleFieldChange('bank', 'bankName', e.target.value)}
                    disabled={isDisabled}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Branch Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter branch name"
                    value={formData.bank.branchName}
                    onChange={(e) => handleFieldChange('bank', 'branchName', e.target.value)}
                    disabled={isDisabled}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>UPI ID <span className="text-muted">(Optional)</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter UPI ID (e.g., seller@upi)"
                    value={formData.bank.upiId}
                    onChange={(e) => handleFieldChange('bank', 'upiId', e.target.value)}
                    disabled={isDisabled}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          {/* CONTACT INFORMATION SECTION */}
          <Col md={6}>
            <Card className="form-card contact">
              <Card.Header>
                <FaPhone /> Contact Information
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number <span className="required">*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Enter phone number"
                    value={formData.contact.phone}
                    onChange={(e) => handleFieldChange('contact', 'phone', e.target.value)}
                    disabled={isDisabled}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Alternate Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Enter alternate phone number"
                    value={formData.contact.alternatePhone}
                    onChange={(e) => handleFieldChange('contact', 'alternatePhone', e.target.value)}
                    disabled={isDisabled}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Enter complete address"
                    value={formData.contact.address}
                    onChange={(e) => handleFieldChange('contact', 'address', e.target.value)}
                    disabled={isDisabled}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter city"
                        value={formData.contact.city}
                        onChange={(e) => handleFieldChange('contact', 'city', e.target.value)}
                        disabled={isDisabled}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter state"
                        value={formData.contact.state}
                        onChange={(e) => handleFieldChange('contact', 'state', e.target.value)}
                        disabled={isDisabled}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Pincode</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter pincode"
                        value={formData.contact.pincode}
                        onChange={(e) => handleFieldChange('contact', 'pincode', e.target.value)}
                        disabled={isDisabled}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Country</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.contact.country}
                        onChange={(e) => handleFieldChange('contact', 'country', e.target.value)}
                        disabled={isDisabled}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* BUSINESS REGISTRATION SECTION */}
          <Col md={6}>
            <Card className="form-card business">
              <Card.Header>
                <FaBriefcase /> Business Registration <small className="ms-2 opacity-75">(Optional)</small>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Registration Type</Form.Label>
                  <Form.Select
                    value={formData.business.registrationType}
                    onChange={(e) => handleFieldChange('business', 'registrationType', e.target.value)}
                    disabled={isDisabled}
                  >
                    <option value="">Select Registration Type</option>
                    <option value="sole_proprietorship">Sole Proprietorship</option>
                    <option value="partnership">Partnership</option>
                    <option value="llp">LLP</option>
                    <option value="private_limited">Private Limited</option>
                    <option value="public_limited">Public Limited</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Registration Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter registration number"
                    value={formData.business.registrationNumber}
                    onChange={(e) => handleFieldChange('business', 'registrationNumber', e.target.value)}
                    disabled={isDisabled}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Registration Certificate</Form.Label>
                  <div className="file-input-wrapper">
                    <Form.Control
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => handleFileUpload('business', 'certificate', e.target.files[0])}
                      disabled={isDisabled}
                    />
                  </div>
                  <FilePreview
                    url={formData.business.certificate}
                    onView={() => window.open(`https://api.brandelvendor.starlighttechlabsindia.com${formData.business.certificate}`, '_blank')}
                    onRemove={() => handleRemoveFile('business', 'certificate')}
                    isDisabled={isDisabled}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Upload Progress */}
        {uploading && (
          <div className="upload-progress">
            <div className="progress">
              <div 
                className="progress-bar" 
                style={{ width: `${uploadProgress}%` }}
                role="progressbar"
                aria-valuenow={uploadProgress}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            <div className="progress-label">
              <span>Uploading...</span>
              <span>{uploadProgress}%</span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons">
          <Button
            variant="primary"
            onClick={autoSave}
            disabled={saving || isDisabled}
          >
            <FaSave /> {saving ? "Saving..." : "Save Draft"}
          </Button>

          <Button
            variant="success"
            onClick={handleSubmit}
            disabled={submitting || isDisabled}
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" />
                Submitting...
              </>
            ) : (
              <>
                <FaCheckCircle /> Submit for Verification
              </>
            )}
          </Button>

          {documentStatus === 'submitted' && (
            <Button variant="warning" disabled>
              <FaClock /> Pending Verification
            </Button>
          )}

          {documentStatus === 'verified' && (
            <Button variant="success" disabled>
              <FaCheckCircle /> Verified
            </Button>
          )}
        </div>

        {/* Help Text */}
        <div className="help-text">
          <p><span className="text-danger">*</span> Required fields. Documents are auto-saved every 2 seconds.</p>
          <p>Supported file types: JPEG, PNG, GIF, WEBP, PDF (Max 10MB)</p>
        </div>
      </Container>
    </section>
  );
};

export default PublicDocumentUpload;